/**
 * Synthia 插件加载器实现
 * 负责插件的动态加载和命令集成
 */

import {
  PluginCommand,
  PluginFunction,
  PluginAPI,
  PluginOptions,
} from './plugin';
import { Command } from 'commander';
import { createLogger, Logger } from '../logger/logger';

/**
 * 插件加载器类
 */
export class PluginLoader {
  private loadedPlugins: Map<string, PluginFunction> = new Map();
  private commander: Command;
  private logger: Logger;
  private hooks: Map<string, Function[]> = new Map();

  constructor(commander: Command, logger?: Logger) {
    this.commander = commander;
    this.logger = logger || createLogger();
  }

  /**
   * 从配置加载所有插件
   */
  async loadPluginsFromConfig(config: any): Promise<void> {
    if (!config.plugins || !Array.isArray(config.plugins)) {
      this.logger.warn('⚠️ 未找到插件配置，跳过插件加载');
      return;
    }

    this.logger.info(`📦 开始加载 ${config.plugins.length} 个插件...`);

    for (const pluginConfig of config.plugins) {
      try {
        await this.loadPluginFromConfig(pluginConfig, config.logger);
      } catch (error) {
        this.logger.warn(`⚠️ 插件加载失败:`, error);
      }
    }

    this.logger.success(`✅ 成功加载 ${this.loadedPlugins.size} 个插件`);
  }

  /**
   * 从配置项加载单个插件
   */
  private async loadPluginFromConfig(
    pluginConfig:
      | string
      | PluginFunction
      | { name: string; options?: Record<string, any>; plugin: PluginFunction },
    loggerConfig?: any
  ): Promise<void> {
    // 如果是函数，直接调用
    if (typeof pluginConfig === 'function') {
      await this.loadFunctionPlugin(
        pluginConfig as PluginFunction,
        {},
        'inline-function' + new Date().getTime(),
        loggerConfig
      );
      return;
    } else if (typeof pluginConfig === 'object' && pluginConfig.plugin) {
      await this.loadFunctionPlugin(
        pluginConfig.plugin,
        {},
        pluginConfig.name,
        loggerConfig
      );
      return;
    } else {
      throw new Error('插件配置格式不正确');
    }
  }

  /**
   * 加载函数式插件
   */
  private async loadFunctionPlugin(
    pluginFunction: PluginFunction,
    options: Record<string, any>,
    pluginPath: string,
    loggerConfig?: any
  ): Promise<void> {
    try {
      // 创建插件 API
      const api = this.createPluginAPI(loggerConfig);

      // 创建插件选项
      const pluginOptions: PluginOptions = {
        config: options,
        project: {
          name: 'synthia-project',
          version: '1.0.0',
          root: process.cwd(),
          description: 'Synthia Engine Project',
        },
        globalOptions: {},
      };

      // 调用插件函数
      await pluginFunction(api, pluginOptions);

      // 记录已加载的插件
      this.loadedPlugins.set(pluginPath, pluginFunction);
    } catch (error) {
      this.logger.warn(`⚠️ 函数式插件加载失败: ${pluginPath}`, error);
    }
  }

  /**
   * 创建插件 API
   */
  private createPluginAPI(loggerConfig?: any): PluginAPI {
    return {
      registerCommand: (command: PluginCommand) => {
        this.registerCommand(null as any, command);
      },
      registerHook: (hookName: string, hook: Function) => {
        if (!this.hooks.has(hookName)) {
          this.hooks.set(hookName, []);
        }
        this.hooks.get(hookName)!.push(hook);
      },
      validateJSON: (_schema: any, _data: any) => {
        // 简单的 JSON 校验实现
        try {
          // 这里可以使用 ajv 或其他 JSON schema 校验库
          return { valid: true, errors: [] };
        } catch (error) {
          return {
            valid: false,
            errors: [error instanceof Error ? error.message : String(error)],
          };
        }
      },
      logger: loggerConfig ? createLogger(loggerConfig) : this.logger,
      fs: {
        readFile: async (filePath: string, encoding?: string) => {
          const fs = await import('fs-extra');
          const result = await fs.readFile(filePath, encoding as any);
          return result.toString();
        },
        writeFile: async (
          filePath: string,
          content: string,
          encoding?: string
        ) => {
          const fs = await import('fs-extra');
          await fs.writeFile(filePath, content, encoding as any);
        },
        exists: async (path: string) => {
          const fs = await import('fs-extra');
          return fs.pathExists(path);
        },
        mkdir: async (path: string, recursive?: boolean) => {
          const fs = await import('fs-extra');
          await fs.mkdir(path, { recursive });
        },
        readdir: async (path: string) => {
          const fs = await import('fs-extra');
          return fs.readdir(path);
        },
        remove: async (path: string) => {
          const fs = await import('fs-extra');
          await fs.remove(path);
        },
        copy: async (src: string, dest: string) => {
          const fs = await import('fs-extra');
          await fs.copy(src, dest);
        },
        move: async (src: string, dest: string) => {
          const fs = await import('fs-extra');
          await fs.move(src, dest);
        },
      },
      http: {
        get: () => Promise.resolve(),
        post: () => Promise.resolve(),
        put: () => Promise.resolve(),
        delete: () => Promise.resolve(),
      },
      cache: {
        set: () => Promise.resolve(),
        get: () => Promise.resolve(),
        delete: () => Promise.resolve(),
        clear: () => Promise.resolve(),
        has: () => Promise.resolve(false),
      },
    };
  }

  /**
   * 注册单个命令
   */
  private registerCommand(_plugin: any, command: PluginCommand): void {
    const cmd = this.commander
      .command(command.name)
      .description(command.description);

    // 添加别名
    if (command.aliases && command.aliases.length > 0) {
      cmd.aliases(command.aliases);
    }

    // 添加选项
    if (command.options && command.options.length > 0) {
      for (const option of command.options) {
        cmd.option(option.flags, option.description, option.defaultValue);
      }
    }

    // 添加参数
    if (command.arguments && command.arguments.length > 0) {
      for (const arg of command.arguments) {
        cmd.argument(arg.name, arg.description);
      }
    }

    // 设置命令执行函数
    cmd.action(command.action);

    // 添加帮助信息
    if (command.help) {
      cmd.addHelpText('after', command.help);
    }

    // 添加示例
    if (command.examples && command.examples.length > 0) {
      const examplesText =
        '\n示例:\n' + command.examples.map(ex => `  ${ex}`).join('\n');
      cmd.addHelpText('after', examplesText);
    }
  }

  /**
   * 获取已加载的插件列表
   */
  getLoadedPlugins(): PluginFunction[] {
    return Array.from(this.loadedPlugins.values());
  }

  /**
   * 执行钩子
   */
  async executeHooks(hookName: string, context?: any): Promise<void> {
    const hooks = this.hooks.get(hookName);
    if (!hooks || hooks.length === 0) return;

    for (const hook of hooks) {
      try {
        await hook(context);
      } catch (error) {
        this.logger.warn(`⚠️ 执行钩子 ${hookName} 失败:`, error);
      }
    }
  }
}
// 导出默认实例
export const globalPluginLoader = new PluginLoader(new Command());
